import { useMemo } from 'react';
import { routes } from '../config/routeSettings';
import allRoutes from '../routes/routes';
import { createLogger } from './logger';

// Create a logger for this module
const logger = createLogger('utils/auth');

/**
 * Utility functions for managing authentication security states and scopes
 * that must persist across page refreshes.
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */

/**
 * Storage keys for security-related state
 */
const STORAGE_KEYS = {
  MFA_ENABLED: 'mfaEnabled',
  DEVICE_VERIFIED: 'deviceVerified',
  SESSION_LOCKED: 'sessionLocked',
  SELECTED_SCOPES: 'scopesSelected',
  USER_ID: 'auth_user_id',
  REDIRECT_URI: 'redirectUri',
  AUTH_ERROR_REASON: 'authErrorReason'
};

/**
 * Get user's scope selections from storage
 * @returns {Array} The user's selected scopes
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const getScopesSelected = () => {
  try {
    const storedScopes = localStorage.getItem(STORAGE_KEYS.SELECTED_SCOPES);
    return storedScopes ? JSON.parse(storedScopes) : [];
  } catch (error) {
    logger.error('Error parsing stored scopes:', error);
    return [];
  }
};

/**
 * Determines if the user is authenticated based on the authentication object.
 *
 * @param {Object} authentication - The authentication object to check.
 * @param {string} [authentication.id] - The user ID.
 * @param {string} [authentication.accessToken] - The access token.
 * @returns {boolean} - Returns true if both ID and access token exist, otherwise false.
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const getAuthStatus = authentication => {
  return !!authentication?.id && !!authentication?.accessToken;
};

/**
 * Extracts user roles from the authentication object.
 *
 * @param {Object} authentication - The authentication object containing user information.
 * @param {Array} [authentication.roles] - An array of roles assigned to the user.
 * @returns {Array} An array of user roles or an empty array if no roles exist.
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const fetchUserRoles = authentication => {
  return authentication.roles.map(role => role.slug) || [];
};

/**
 * Store user's scope selections
 * @param {Array} scopes - The scopes to store
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const storeScopesSelected = scopes => {
  if (!Array.isArray(scopes)) return;
  localStorage.setItem(STORAGE_KEYS.SELECTED_SCOPES, JSON.stringify(scopes));
};

/**
 * Checks the validity of authentication scopes
 * @param {Object} authentication - The authentication object
 * @param {boolean} isAuthFlowRoute - Whether the current route is an auth flow route
 * @returns {Object} - Scope validation status
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const validateAuthScopes = (authentication, isAuthFlowRoute) => {
  // If no auth, no need to check scopes
  if (!authentication?.id) {
    return { valid: false, message: 'No authentication' };
  }

  // Skip scope validation for auth flow routes - allow continuing the flow
  if (isAuthFlowRoute) {
    return { valid: true, message: 'Skipping scope validation for auth flow' };
  }

  // Skip scope validation while still in an auth flow
  if (
    !authentication?.security?.multifactor?.app?.isEnabled ||
    authentication.session?.device?.isVerified !== true
  ) {
    return {
      valid: true,
      message: 'Auth flow incomplete, skipping scope validation'
    };
  }

  // Check that scopesSelected exists and has items
  if (!Array.isArray(authentication.scopesSelected)) {
    return { valid: false, message: 'Scopes not initialized' };
  }

  // Past this point, we're fully authenticated and should have valid scopes

  // Check for empty scopes
  if (authentication.scopesSelected.length === 0) {
    return { valid: false, message: 'No scopes selected' };
  }

  // Less strict scope validation - accept any non-empty array with objects
  const hasAnyScopes = authentication.scopesSelected.some(
    scope => scope && typeof scope === 'object'
  );

  if (!hasAnyScopes) {
    return { valid: false, message: 'Invalid scope objects detected' };
  }

  // Store valid scope IDs in localStorage for debugging
  try {
    const scopeIds = authentication.scopesSelected
      .filter(scope => scope && scope.id)
      .map(scope => scope.id);

    if (scopeIds.length > 0) {
      localStorage.setItem('debug_scopes', JSON.stringify(scopeIds));
    }
  } catch (e) {
    // Ignore localStorage errors
  }

  return { valid: true, message: 'Scopes valid' };
};

/**
 * Hook for checking authentication scope validity
 * @param {Object} authentication - The authentication object
 * @param {boolean} isAuthFlowRoute - Whether we're on an auth flow route
 * @returns {Object} - Scope validation status
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const useScopeStatus = (authentication, isAuthFlowRoute) => {
  return useMemo(
    () => validateAuthScopes(authentication, isAuthFlowRoute),
    [
      authentication?.id,
      authentication?.scopesSelected,
      authentication?.security?.multifactor?.app?.isEnabled,
      authentication?.session?.device?.isVerified,
      isAuthFlowRoute
    ]
  );
};

/**
 * Determines if a user has access to a specific scope
 * @param {Array} userScopes - The user's scopes
 * @param {string|Array} requiredScopes - The required scope(s) for access
 * @param {Object} options - Options for access check
 * @returns {boolean} - True if user has access
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const hasScope = (userScopes, requiredScopes, options = {}) => {
  const { requireAll = false } = options;

  if (!Array.isArray(userScopes) || userScopes.length === 0) {
    return false;
  }

  // Handle case of single scope requirement
  if (typeof requiredScopes === 'string') {
    requiredScopes = [requiredScopes];
  }

  if (!Array.isArray(requiredScopes) || requiredScopes.length === 0) {
    return true; // No requirements means automatic access
  }

  // Extract scope IDs for easier comparison
  const userScopeIds = userScopes
    .filter(scope => scope && scope.id)
    .map(scope => scope.id);

  if (requireAll) {
    // User must have ALL required scopes
    return requiredScopes.every(scope => userScopeIds.includes(scope));
  } else {
    // User must have ANY of the required scopes
    return requiredScopes.some(scope => userScopeIds.includes(scope));
  }
};

/**
 * Normalizes scope objects to ensure they have a consistent id property
 * Handles backend responses that might use _id instead of id
 *
 * @param {Array} scopes - Array of scope objects to normalize
 * @returns {Array} - Array of normalized scope objects
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const normalizeScopes = scopes => {
  if (!Array.isArray(scopes)) return [];

  return scopes
    .map(scope => {
      if (!scope) return null;

      // Create a copy to avoid mutating the original
      const normalizedScope = { ...scope };

      // If scope has _id but not id, copy _id to id
      if (normalizedScope._id && !normalizedScope.id) {
        normalizedScope.id = normalizedScope._id;
      }

      return normalizedScope;
    })
    .filter(Boolean);
};

/**
 * Validates scope objects to ensure they have required properties
 * @param {Object} scope - The scope object to validate
 * @returns {boolean} - Whether the scope is valid
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const validateScope = scope => {
  return scope && typeof scope === 'object' && (scope.id || scope.scopeId);
};

/**
 * Validates selected scopes against available scopes from the backend
 * to prevent scope spoofing and handle revoked scopes
 *
 * @param {Array} selectedScopes - User selected scopes
 * @param {Array} availableScopes - Scopes available from backend
 * @returns {Array} - Filtered valid scopes
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const validateScopes = (selectedScopes, availableScopes) => {
  if (!Array.isArray(selectedScopes) || !Array.isArray(availableScopes)) {
    logger.warn('Invalid scope arrays provided to validateScopes', {
      selectedScopes: Array.isArray(selectedScopes),
      availableScopes: Array.isArray(availableScopes)
    });
    return [];
  }

  if (availableScopes.length === 0) {
    logger.warn('No available scopes provided to validateScopes');
    return [];
  }

  // Normalize scopes to ensure consistent id property
  const normalizedAvailableScopes = normalizeScopes(availableScopes);
  const normalizedSelectedScopes = normalizeScopes(selectedScopes);

  // Map valid scope IDs -> authoritative backend scope objects for fast lookup.
  // Selected scopes are refreshed from these objects so newly added backend
  // fields (e.g. ingestId) propagate instead of reusing stale stored copies.
  const availableScopeById = new Map(
    normalizedAvailableScopes
      .filter(scope => scope && scope.id)
      .map(scope => [scope.id, scope])
  );

  // Keep only selected scopes still offered by the backend, replacing each with
  // the authoritative backend object to avoid stale field drift.
  const validatedScopes = normalizedSelectedScopes
    .filter(scope => scope && scope.id && availableScopeById.has(scope.id))
    .map(scope => availableScopeById.get(scope.id));

  // Log the validation results
  logger.debug(
    `Scope validation: ${validatedScopes.length}/${normalizedSelectedScopes.length} scopes are valid`
  );

  return validatedScopes;
};

/**
 * Merge new scopes with stored scopes, with preference for user selections
 * but filtering out any invalid scopes not provided by the backend
 *
 * @param {Array} availableScopes - All available scopes from the server
 * @param {Array|null} currentSelections - Currently selected scopes (null if none)
 * @returns {Array} - Merged scopes list
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const mergeScopes = (availableScopes, currentSelections) => {
  // If available scopes is not an array or empty, return empty array
  if (!Array.isArray(availableScopes) || availableScopes.length === 0) {
    logger.warn('No available scopes provided to mergeScopes');
    return [];
  }

  // Normalize available scopes to ensure consistent id property
  const normalizedAvailableScopes = normalizeScopes(availableScopes);

  // If we have user selections, validate and use them
  if (currentSelections && currentSelections.length > 0) {
    return validateScopes(currentSelections, normalizedAvailableScopes);
  }

  // If we have stored selections, validate and use those
  const storedScopes = getScopesSelected();
  if (storedScopes.length > 0) {
    const validScopes = validateScopes(storedScopes, normalizedAvailableScopes);
    if (validScopes.length > 0) {
      return validScopes;
    }
  }

  // Otherwise, use available scopes as default (normalized)
  return normalizedAvailableScopes;
};

/**
 * Retrieves the stored security state from localStorage.
 * @returns {Object} The stored security state
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const getStoredSecurityState = () => {
  return {
    mfaEnabled: localStorage.getItem(STORAGE_KEYS.MFA_ENABLED) === 'true',
    deviceVerified:
      localStorage.getItem(STORAGE_KEYS.DEVICE_VERIFIED) === 'true',
    sessionLocked: localStorage.getItem(STORAGE_KEYS.SESSION_LOCKED) === 'true',
    scopesSelected: getScopesSelected()
  };
};

/**
 * Applies the stored security state to the provided authentication state.
 * @param {Object} authState - The current authentication state
 * @returns {Object} The updated authentication state with applied security state
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const applyStoredSecurityState = authState => {
  const securityState = getStoredSecurityState();

  // Determine which scopes to use (preserve user selections if they exist)
  const scopesToUse = mergeScopes(authState.scopes, authState.scopesSelected);

  return {
    ...authState,
    security: {
      ...(authState.security || {}),
      multifactor: {
        ...(authState.security?.multifactor || {}),
        app: {
          ...(authState.security?.multifactor?.app || {}),
          isEnabled:
            authState.security?.multifactor?.app?.isEnabled !== undefined
              ? authState.security.multifactor.app.isEnabled
              : securityState.mfaEnabled
        }
      }
    },
    session: {
      ...(authState.session || {}),
      device: {
        ...(authState.session?.device || {}),
        isVerified:
          authState.session?.device?.isVerified !== undefined
            ? authState.session.device.isVerified
            : securityState.deviceVerified,
        sessionLocked:
          authState.session?.device?.sessionLocked !== undefined
            ? authState.session.device.sessionLocked
            : securityState.sessionLocked
      }
    },
    scopesSelected: scopesToUse
  };
};

/**
 * Stores the security state in localStorage.
 * @param {Object} authState - The current authentication state
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const storeSecurityState = authState => {
  if (!authState) return;

  // Store MFA status
  if (authState.security?.multifactor?.app?.isEnabled !== undefined) {
    localStorage.setItem(
      STORAGE_KEYS.MFA_ENABLED,
      authState.security.multifactor.app.isEnabled ? 'true' : 'false'
    );
  }

  // Store device verification status
  if (authState.session?.device?.isVerified !== undefined) {
    localStorage.setItem(
      STORAGE_KEYS.DEVICE_VERIFIED,
      authState.session.device.isVerified ? 'true' : 'false'
    );
  }

  // Store session lock status - also store in direct 'sessionLocked' key for easier access
  if (authState.session?.device?.sessionLocked !== undefined) {
    localStorage.setItem(
      STORAGE_KEYS.SESSION_LOCKED,
      authState.session.device.sessionLocked ? 'true' : 'false'
    );

    // Set or remove the direct access key based on lock status
    if (authState.session.device.sessionLocked) {
      localStorage.setItem('sessionLocked', 'true');
    } else {
      localStorage.removeItem('sessionLocked');
    }
  }

  // Store selected scopes
  if (authState.scopesSelected) {
    storeScopesSelected(authState.scopesSelected);
  }
};

/**
 * Handles unauthorized access by redirecting to the 401 error page
 * and preserving the attempted URL for redirecting back after authentication.
 *
 * @param {Object} options - Options for the redirect
 * @param {string} [options.attemptedUrl] - The URL the user was trying to access
 * @param {string} [options.reason='token_refresh_failed'] - Optional reason for the redirect for analytics
 * @param {boolean} [options.force=false] - Force the redirect even if we're already on an auth/error page
 * @param {Function} [options.navigate] - React Router's navigate function for client-side routing (optional)
 * @returns {void}
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.8
 * @since 0.1.0-beta.7
 */
export const handleUnauthorizedAccess = (options = {}) => {
  const {
    attemptedUrl = window.location.pathname + window.location.search,
    reason = 'token_refresh_failed',
    force = false,
    navigate // This is now optional
  } = options;

  // Don't redirect if we're already on an auth page or error page unless forced
  const isAuthOrErrorPage =
    attemptedUrl.includes('/auth/') ||
    attemptedUrl.includes('/error/') ||
    attemptedUrl === '/login' ||
    attemptedUrl === '/lock';

  if (isAuthOrErrorPage && !force) {
    logger.warn(`Skipping unauthorized redirect on ${attemptedUrl}: ${reason}`);
    return;
  }

  // Store the attempted URL to redirect back after login
  localStorage.setItem('redirectUri', attemptedUrl);

  // Store the reason for unauthorized access (could be used for analytics or debugging)
  localStorage.setItem('authErrorReason', reason);

  // Log the redirect for debugging
  logger.warn(`Redirecting to 401 page: ${reason}`);

  // If navigate function is provided, use it for client-side routing
  if (typeof navigate === 'function') {
    navigate(routes.ERROR_401, { replace: true });
  } else {
    // Fall back to window.location for cases where navigate isn't available
    window.location.href = routes.ERROR_401;
  }
};

/**
 * Applies authentication and role-based authorization requirements to a route tree.
 *
 * This function traverses through a nested route structure and applies auth requirements
 * based on provided options. It can preserve existing role configurations or override them.
 * The function also checks against a global routes definition (allRoutes) to respect
 * predefined authorization settings.
 *
 * @param {Array} routes - The array of route objects to process
 * @param {Object} [options={}] - Configuration options for auth requirements
 * @param {boolean} [options.authRequired=true] - Whether authentication is required for routes
 * @param {Array<string>} [options.roles=['user', 'admin', 'super-admin']] - Default roles to apply
 * @param {boolean} [options.preserveExistingRoles=true] - Whether to keep existing role configs when present
 *
 * @returns {Array} - The processed routes with auth requirements applied
 *
 * @example
 * const routes = [
 *   { to: '/dashboard', children: [{ to: '/dashboard/settings' }] }
 * ];
 *
 * const securedRoutes = applyAuthRequirements(routes, {
 *   roles: ['admin'],
 *   preserveExistingRoles: false
 * });
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const applyAuthRequirements = (routes, options = {}) => {
  const {
    authRequired = true,
    roles = ['user', 'admin', 'super-admin'],
    preserveExistingRoles = true
  } = options;

  /**
   * Flattens a nested route array into a single-level array.
   *
   * @param {Array} routeArray - The array of route objects which may contain nested children.
   * @returns {Array} A flattened array of route objects, each containing a path and allowedRoles.
   *
   * @example
   * const routes = [
   *   { to: '/home', allowedRoles: ['user', 'admin'] },
   *   {
   *     to: '/admin',
   *     allowedRoles: ['admin'],
   *     children: [
   *       { to: '/admin/users', allowedRoles: ['admin'] }
   *     ]
   *   }
   * ];
   * const flattened = flattenRoutes(routes);
   * // Returns:
   * // [
   * //   { path: '/home', allowedRoles: ['user', 'admin'] },
   * //   { path: '/admin', allowedRoles: ['admin'] },
   * //   { path: '/admin/users', allowedRoles: ['admin'] }
   * // ]
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  const flattenRoutes = routeArray => {
    let flatRoutes = [];

    /**
     * Processes a route object and adds it to the flatRoutes array.
     * Recursively processes any child routes if they exist.
     *
     * @param {Object} route - The route object to process
     * @param {string} [route.to] - The path of the route
     * @param {Array<string>} [route.allowedRoles] - Roles that are allowed to access this route
     * @param {Array<Object>} [route.children] - Child routes to process recursively
     */
    const processRoute = route => {
      // Add the current route
      if (route.to) {
        flatRoutes.push({
          path: route.to,
          allowedRoles: route.allowedRoles || []
        });
      }

      // Process children recursively if they exist
      if (Array.isArray(route.children)) {
        route.children.forEach(processRoute);
      }
    };

    // Process each top-level route
    routeArray.forEach(processRoute);
    return flatRoutes;
  };

  /**
   * A map that associates route paths with their allowed user roles.
   *
   * This object is constructed by flattening all routes and creating a lookup
   * where each key is a route path and its value is an array of allowed roles
   * for that specific route.
   *
   * @type {Object<string, Array<string>>}
   * @example
   * // Example structure:
   * // {
   * //   '/dashboard': ['admin', 'user'],
   * //   '/settings': ['admin'],
   * //   '/profile': ['admin', 'user', 'guest']
   * // }
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  const routeRolesMap = flattenRoutes(allRoutes).reduce((lookup, route) => {
    lookup[route.path] = route.allowedRoles;
    return lookup;
  }, {});

  /**
   * Recursively applies authentication configuration to routes and their children.
   *
   * @param {Array} routesToProcess - The array of route objects to process
   * @returns {Array} - Array of routes with updated authentication properties
   *
   * This function examines each route and:
   * 1. Checks for predefined roles in routeRolesMap based on the route's 'to' property
   * 2. Determines which roles to apply based on a priority chain:
   *    - First: predefined roles from routeRolesMap (if available)
   *    - Second: existing roles on the route (if preserveExistingRoles is true)
   *    - Third: default roles from the parent context
   * 3. Adds authRequired to each route
   * 4. Recursively processes any children routes with the same logic
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  const applyToRoutes = routesToProcess => {
    return routesToProcess.map(route => {
      // Determine the key to use for predefined role lookup. Prefer 'path' (used in router configs),
      // fall back to 'to' (used in menu/navigation configs like allRoutes)
      const routeKey = route.path || route.to;

      // Get the predefined allowedRoles for this route path if they exist
      const predefinedRoles = routeKey ? routeRolesMap[routeKey] : null;

      // Always use predefinedRoles from allRoutes if available
      // Otherwise, fall back to the priority chain
      const appliedRoles =
        predefinedRoles?.length > 0
          ? predefinedRoles
          : preserveExistingRoles && route.roles
            ? route.roles
            : roles;

      // Create the updated route object
      const updatedRoute = {
        ...route,
        authRequired,
        roles: appliedRoles
      };

      // Process child routes recursively if present
      if (Array.isArray(route.children)) {
        updatedRoute.children = applyToRoutes(route.children);
      }

      return updatedRoute;
    });
  };

  // Apply roles recursively to all routes and their children
  return applyToRoutes(routes);
};

/**
 * Stores the user ID in localStorage for persistence
 *
 * @param {string} userId - The user ID to store
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const storeUserId = userId => {
  if (userId) {
    localStorage.setItem(STORAGE_KEYS.USER_ID, userId);
  } else {
    localStorage.removeItem(STORAGE_KEYS.USER_ID);
  }
};

/**
 * Retrieves the stored user ID
 *
 * @returns {string|null} The stored user ID or null
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const getUserId = () => {
  return localStorage.getItem(STORAGE_KEYS.USER_ID);
};

/**
 * Saves the redirect location before authentication/authorization
 *
 * @param {string} [currentPath] - The current path to save (defaults to window.location.pathname)
 * @param {Object} options - Additional options
 * @param {boolean} options.force - Whether to force saving auth routes (default: false)
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const saveRedirectLocation = (currentPath = null, options = {}) => {
  const { force = false } = options;
  const path = currentPath || window.location.pathname + window.location.search;

  // Don't save auth routes or error pages as redirect locations unless forced
  if (!force && (path.includes('/auth/') || path.includes('/error/'))) {
    return;
  }

  localStorage.setItem(STORAGE_KEYS.REDIRECT_URI, path);
};

/**
 * Gets the saved redirect location
 *
 * @param {string} defaultPath - The default path if no redirect is stored
 * @returns {string} The saved redirect location or the default route
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const getRedirectLocation = (defaultPath = '/overview') => {
  const redirectUri = localStorage.getItem(STORAGE_KEYS.REDIRECT_URI);
  return redirectUri || defaultPath;
};

/**
 * Clears the saved redirect location
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const clearRedirectLocation = () => {
  localStorage.removeItem(STORAGE_KEYS.REDIRECT_URI);
};

/**
 * Stores the reason for an auth error
 *
 * @param {string} reason - The error reason
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const storeAuthErrorReason = reason => {
  if (reason) {
    localStorage.setItem(STORAGE_KEYS.AUTH_ERROR_REASON, reason);
  } else {
    localStorage.removeItem(STORAGE_KEYS.AUTH_ERROR_REASON);
  }
};

/**
 * Clears all authentication storage
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const clearAuthStorage = () => {
  localStorage.removeItem(STORAGE_KEYS.USER_ID);
  localStorage.removeItem(STORAGE_KEYS.REDIRECT_URI);
  localStorage.removeItem(STORAGE_KEYS.AUTH_ERROR_REASON);
};

/**
 * Checks if a user's access is forbidden based on their authentication status.
 *
 * This function examines the authentication object to determine if the user
 * has been explicitly marked as forbidden from accessing the application,
 * such as when an account is suspended, banned, or has access revoked.
 *
 * @param {Object} authentication - The authentication object containing user information
 * @param {boolean} [authentication.forbidden] - Flag indicating if the user's access is forbidden
 * @returns {boolean} True if access is forbidden, false otherwise
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.8
 * @since 0.1.0-beta.8
 */
export const checkForbiddenStatus = authentication => {
  // Check if the user is explicitly marked as forbidden
  if (authentication?.forbidden === true) {
    return true;
  }

  // Check if the user has an access restriction flag
  if (authentication?.security?.accessRestricted === true) {
    return true;
  }

  // No forbidden conditions met
  return false;
};
